import { useState, type ChangeEvent } from 'react';
import { useLandmarkStore } from '../state/landmarks';
import { HISTORICAL_OVERLAYS } from '../lib/historical-overlays';
import { signIn, signOut } from '../api/artist-pois';

interface LayerPanelProps {
  onOpenHistoricalMaps?: () => void;
  onOpenSplatsGallery?: () => void;
}

/**
 * Bottom-left collapsible panel. Each overlay row: checkbox + opacity slider.
 * Slider only enabled when the overlay's checkbox is checked.
 *
 * The panel is collapsed by default to keep the map free of chrome at first
 * glance — clicking the header expands it. State is local (component-only),
 * not persisted; only visibility + opacity persist (in the store/localStorage).
 */
export function LayerPanel({ onOpenHistoricalMaps, onOpenSplatsGallery }: LayerPanelProps = {}) {
  const overlays = useLandmarkStore((state) => state.overlays);
  const toggleOverlay = useLandmarkStore((state) => state.toggleOverlay);
  const setOverlayOpacity = useLandmarkStore((state) => state.setOverlayOpacity);
  const [expanded, setExpanded] = useState(false);

  const visibleCount = Object.values(overlays).filter((o) => o.visible).length;

  return (
    <div
      data-testid="layer-panel"
      style={{
        position: 'absolute',
        bottom: 'var(--space-12)',
        left: 'var(--space-3)',
        width: expanded ? 'min(280px, calc(100vw - var(--space-6)))' : 'auto',
        // Cap the body so the panel can't grow up past the search bar at the
        // top. The header always stays visible.
        maxHeight: 'calc(100vh - 96px)',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--surface-raised)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-card)',
        zIndex: 15,
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
        aria-controls="layer-panel-body"
        data-testid="layer-panel-toggle"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          width: '100%',
          padding: '8px 12px',
          background: 'transparent',
          color: 'var(--text)',
          fontSize: '0.85rem',
          fontWeight: 600,
          textAlign: 'left',
        }}
      >
        <svg
          width={16}
          height={16}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.75}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          focusable="false"
        >
          <polygon points="12 2 22 8.5 12 15 2 8.5 12 2" />
          <polyline points="2 15.5 12 22 22 15.5" />
        </svg>
        <span>Historical maps</span>
        {visibleCount > 0 ? (
          <span
            data-testid="layer-panel-count"
            style={{
              marginLeft: 'auto',
              fontSize: '0.7rem',
              padding: '1px 8px',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--accent)',
              color: '#1a1410',
              fontWeight: 700,
            }}
          >
            {visibleCount}
          </span>
        ) : null}
        <svg
          width={14}
          height={14}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          focusable="false"
          style={{
            marginLeft: visibleCount > 0 ? 'var(--space-1)' : 'auto',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 150ms ease',
          }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {expanded ? (
        <div
          id="layer-panel-body"
          data-testid="layer-panel-body"
          style={{
            borderTop: '1px solid var(--border)',
            padding: 'var(--space-3)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-3)',
            overflowY: 'auto',
            // Scrolling inside the body keeps the header visible at all times.
            minHeight: 0,
          }}
        >
          <AdminTokenRow />
          <BaseLayerSwitcher />
          <PinLayerToggles />
          <PaperFrameControls />
          {onOpenHistoricalMaps ? (
            <button
              type="button"
              onClick={onOpenHistoricalMaps}
              data-testid="open-historical-maps"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                padding: '8px 12px',
                background: 'var(--surface)',
                color: 'var(--text)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                fontSize: '0.85rem',
                fontWeight: 600,
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <svg
                width={16}
                height={16}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.75}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                focusable="false"
              >
                <rect x="3" y="4" width="18" height="14" rx="2" />
                <path d="M3 9h18" />
                <path d="M8 4v14" />
              </svg>
              Historical maps of Antwerp
            </button>
          ) : null}
          {onOpenSplatsGallery ? (
            <button
              type="button"
              onClick={onOpenSplatsGallery}
              data-testid="open-splats-gallery"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                padding: '8px 12px',
                background: 'var(--surface)',
                color: 'var(--text)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                fontSize: '0.85rem',
                fontWeight: 600,
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <svg
                width={16}
                height={16}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.75}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                focusable="false"
              >
                <path d="M12 2 2 7l10 5 10-5-10-5z" />
                <path d="m2 17 10 5 10-5" />
                <path d="m2 12 10 5 10-5" />
              </svg>
              3D Views
            </button>
          ) : null}
          <div
            style={{
              fontSize: '0.7rem',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'var(--text-muted)',
              fontWeight: 600,
              marginTop: 'var(--space-1)',
            }}
          >
            Historical overlays
          </div>
          {HISTORICAL_OVERLAYS.map((overlay) => {
            const state = overlays[overlay.id];
            const rowProps: OverlayRowProps = {
              id: overlay.id,
              name: overlay.name,
              year: overlay.year,
              visible: state.visible,
              opacity: state.opacity,
              onToggle: () => toggleOverlay(overlay.id),
              onOpacityChange: (value: number) => setOverlayOpacity(overlay.id, value),
            };
            if (overlay.coverageNote) rowProps.coverageNote = overlay.coverageNote;
            return <OverlayRow key={overlay.id} {...rowProps} />;
          })}
        </div>
      ) : null}
    </div>
  );
}

function BaseLayerSwitcher() {
  const baseLayer = useLandmarkStore((state) => state.baseLayer);
  const setBaseLayer = useLandmarkStore((state) => state.setBaseLayer);
  return (
    <div data-testid="base-layer-switcher">
      <div
        style={{
          fontSize: '0.7rem',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'var(--text-muted)',
          fontWeight: 600,
          marginBottom: 'var(--space-2)',
        }}
      >
        Base map
      </div>
      <div role="radiogroup" aria-label="Base map" style={{ display: 'flex', gap: 'var(--space-1)' }}>
        {(['modern', 'satellite'] as const).map((id) => {
          const active = baseLayer === id;
          return (
            <button
              key={id}
              type="button"
              role="radio"
              aria-checked={active}
              data-testid={`base-layer-${id}`}
              onClick={() => setBaseLayer(id)}
              style={{
                flex: 1,
                padding: '6px 10px',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border)',
                background: active ? 'var(--primary)' : 'var(--surface)',
                color: active ? '#fffdf2' : 'var(--text)',
                fontSize: '0.8rem',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {id === 'modern' ? 'Modern' : 'Satellite'}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function PinLayerToggles() {
  const heritageVisible = useLandmarkStore((state) => state.heritageVisible);
  const toggleHeritage = useLandmarkStore((state) => state.toggleHeritage);
  const antwerpHistoricalVisible = useLandmarkStore((state) => state.antwerpHistoricalVisible);
  const toggleAntwerpHistorical = useLandmarkStore((state) => state.toggleAntwerpHistorical);
  const artistSelectionOnly = useLandmarkStore((state) => state.artistSelectionOnly);
  const toggleArtistSelectionOnly = useLandmarkStore((state) => state.toggleArtistSelectionOnly);
  const artistSelectionSize = useLandmarkStore((state) => state.artistSelection.size);

  const antwerpSubLabel = antwerpHistoricalVisible
    ? 'Curated landmarks across Flanders'
    : 'Curated landmarks across Flanders (off)';

  return (
    <div data-testid="pin-layer-toggles">
      <div
        style={{
          fontSize: '0.7rem',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'var(--text-muted)',
          fontWeight: 600,
          marginBottom: 'var(--space-2)',
        }}
      >
        Pin layers
      </div>
      <PinToggleRow
        id="heritage"
        label="Heritage objects"
        sublabel="Onroerend Erfgoed (~80k pins)"
        checked={heritageVisible}
        onChange={toggleHeritage}
      />
      <PinToggleRow
        id="antwerp-historical"
        label="Historical Flanders"
        sublabel={antwerpSubLabel}
        checked={antwerpHistoricalVisible}
        onChange={toggleAntwerpHistorical}
      />
      <PinToggleRow
        id="artist-selection-only"
        label="Artist selection only"
        sublabel={`Show only the ${artistSelectionSize} flagged landmarks`}
        checked={artistSelectionOnly}
        onChange={toggleArtistSelectionOnly}
        disabled={!antwerpHistoricalVisible}
      />
    </div>
  );
}

function PaperFrameControls() {
  const visible = useLandmarkStore((state) => state.paperFrameVisible);
  const toggleVisible = useLandmarkStore((state) => state.togglePaperFrameVisible);
  const opacity = useLandmarkStore((state) => state.paperFrameOpacity);
  const setOpacity = useLandmarkStore((state) => state.setPaperFrameOpacity);
  const borderVisible = useLandmarkStore((state) => state.paperFrameBorderVisible);
  const toggleBorder = useLandmarkStore((state) => state.togglePaperFrameBorder);
  const shortSide = useLandmarkStore((state) => state.paperFrameShortSideMeters);
  const rotation = useLandmarkStore((state) => state.paperFrameRotation);
  const reset = useLandmarkStore((state) => state.resetPaperFrame);
  const isAdmin = useLandmarkStore((state) => state.isAdmin);
  const locked = useLandmarkStore((state) => state.paperFrameLocked);
  const saving = useLandmarkStore((state) => state.paperFrameSaving);
  const publish = useLandmarkStore((state) => state.publishPaperFrame);

  // Non-admins who haven't been published a frame yet shouldn't see a confusing
  // "Paper frame" section. The overlay component already hides itself; mirror
  // that here so the panel stays clean for the artist.
  const serverLoaded = useLandmarkStore((state) => state.paperFrameServerLoaded);
  if (!isAdmin && (!serverLoaded || !locked)) return null;

  const widthLabel = shortSide >= 1000 ? `${(shortSide / 1000).toFixed(2)} km` : `${Math.round(shortSide)} m`;
  const longSideMeters = shortSide * Math.SQRT2;
  const heightLabel = longSideMeters >= 1000 ? `${(longSideMeters / 1000).toFixed(2)} km` : `${Math.round(longSideMeters)} m`;
  const sublabel = locked
    ? `Locked · ${widthLabel} × ${heightLabel}`
    : `Draft · ${widthLabel} × ${heightLabel}`;

  const handleLockClick = () => {
    void publish(!locked).catch((err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Save failed';
      // eslint-disable-next-line no-alert
      window.alert(msg);
    });
  };

  return (
    <div data-testid="paper-frame-controls">
      <div
        style={{
          fontSize: '0.7rem',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'var(--text-muted)',
          fontWeight: 600,
          marginBottom: 'var(--space-2)',
        }}
      >
        Artist paper frame
      </div>
      <PinToggleRow
        id="paper-frame"
        label="Paper frame"
        sublabel={sublabel}
        checked={visible}
        onChange={toggleVisible}
      />
      <div style={{ opacity: visible ? 1 : 0.4, pointerEvents: visible ? 'auto' : 'none' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            marginTop: 'var(--space-1)',
          }}
        >
          <label
            htmlFor="paper-frame-opacity"
            style={{
              flex: 1,
              fontSize: '0.75rem',
              color: 'var(--text)',
              cursor: 'pointer',
            }}
          >
            Fill transparency
          </label>
          <span
            aria-hidden="true"
            style={{
              fontSize: '0.7rem',
              color: 'var(--text-muted)',
              fontVariantNumeric: 'tabular-nums',
              minWidth: 32,
              textAlign: 'right',
            }}
          >
            {Math.round(opacity * 100)}%
          </span>
        </div>
        <input
          id="paper-frame-opacity"
          type="range"
          min={0}
          max={0.6}
          step={0.02}
          value={opacity}
          onChange={(e) => setOpacity(Number.parseFloat(e.target.value))}
          data-testid="paper-frame-opacity"
          aria-label="Paper frame fill opacity"
          aria-valuenow={Math.round(opacity * 100)}
          aria-valuemin={0}
          aria-valuemax={60}
          style={{
            width: '100%',
            marginTop: 'var(--space-1)',
            accentColor: 'var(--primary)',
            cursor: 'pointer',
          }}
        />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            marginTop: 'var(--space-2)',
          }}
        >
          <input
            id="paper-frame-border"
            type="checkbox"
            checked={borderVisible}
            onChange={toggleBorder}
            data-testid="paper-frame-border"
            style={{ width: 16, height: 16, accentColor: 'var(--primary)' }}
          />
          <label
            htmlFor="paper-frame-border"
            style={{ flex: 1, fontSize: '0.8rem', cursor: 'pointer' }}
          >
            Show border outline
          </label>
        </div>
        {isAdmin ? (
          <>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: 'var(--space-2)',
                fontSize: '0.7rem',
                color: 'var(--text-muted)',
              }}
            >
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                Rotation {Math.round(rotation)}°
              </span>
              <button
                type="button"
                onClick={reset}
                disabled={locked || saving}
                data-testid="paper-frame-reset"
                style={{
                  background: 'transparent',
                  color: 'var(--text-muted)',
                  border: 'none',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  cursor: locked || saving ? 'not-allowed' : 'pointer',
                  textDecoration: 'underline',
                  padding: 0,
                }}
              >
                Reset position
              </button>
            </div>
            <button
              type="button"
              onClick={handleLockClick}
              disabled={saving}
              data-testid="paper-frame-lock"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'var(--space-2)',
                width: '100%',
                marginTop: 'var(--space-2)',
                padding: '8px 12px',
                background: locked ? 'var(--surface)' : 'var(--primary)',
                color: locked ? 'var(--text)' : '#fffdf2',
                border: locked ? '1px solid var(--border)' : '1px solid var(--primary)',
                borderRadius: 'var(--radius-sm)',
                fontSize: '0.8rem',
                fontWeight: 700,
                cursor: saving ? 'wait' : 'pointer',
              }}
            >
              <svg
                width={14}
                height={14}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                {locked ? (
                  <>
                    <rect x="3" y="11" width="18" height="11" rx="2" />
                    <path d="M7 11V8a5 5 0 0 1 9.9-1" />
                  </>
                ) : (
                  <>
                    <rect x="3" y="11" width="18" height="11" rx="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </>
                )}
              </svg>
              {saving
                ? 'Saving…'
                : locked
                ? 'Unlock to edit'
                : 'Lock & publish to artist'}
            </button>
            <div
              style={{
                marginTop: 'var(--space-1)',
                fontSize: '0.7rem',
                color: 'var(--text-muted)',
                lineHeight: 1.35,
              }}
            >
              {locked
                ? 'Visible to anyone on belgium.grunk.li. Drag handles disabled.'
                : 'Draft — only signed-in users see it. Lock to share the position with the artist.'}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

function AdminTokenRow() {
  const isAdmin = useLandmarkStore((state) => state.isAdmin);
  const currentUserEmail = useLandmarkStore((state) => state.currentUserEmail);

  if (isAdmin) {
    return (
      <div
        data-testid="admin-token-row"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          padding: 'var(--space-2) var(--space-3)',
          borderRadius: 'var(--radius-sm)',
          background: 'rgba(56, 142, 60, 0.12)',
          border: '1px solid rgba(56, 142, 60, 0.4)',
        }}
      >
        <svg
          width={14}
          height={14}
          viewBox="0 0 24 24"
          fill="none"
          stroke="#388e3c"
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
        <span
          style={{
            flex: 1,
            fontSize: '0.75rem',
            color: 'var(--text)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={currentUserEmail ?? ''}
        >
          {currentUserEmail}
        </span>
        <button
          type="button"
          onClick={signOut}
          style={{
            background: 'transparent',
            color: 'var(--text-muted)',
            border: 'none',
            fontSize: '0.7rem',
            fontWeight: 600,
            cursor: 'pointer',
            textDecoration: 'underline',
            padding: 0,
          }}
        >
          Sign out
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={signIn}
      data-testid="admin-token-row"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--space-2)',
        padding: '8px 12px',
        background: 'var(--primary)',
        color: '#fffdf2',
        border: '1px solid var(--primary)',
        borderRadius: 'var(--radius-sm)',
        fontSize: '0.85rem',
        fontWeight: 700,
        cursor: 'pointer',
      }}
    >
      <svg
        width={14}
        height={14}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
        <polyline points="10 17 15 12 10 7" />
        <line x1="15" y1="12" x2="3" y2="12" />
      </svg>
      Sign in to edit
    </button>
  );
}

function PinToggleRow({
  id,
  label,
  sublabel,
  checked,
  onChange,
  disabled = false,
}: {
  id: string;
  label: string;
  sublabel: string;
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
}) {
  const checkboxId = `pin-${id}-checkbox`;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        marginBottom: 'var(--space-2)',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <input
        id={checkboxId}
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        data-testid={`pin-toggle-${id}`}
        style={{ width: 16, height: 16, accentColor: 'var(--primary)' }}
      />
      <label
        htmlFor={checkboxId}
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          cursor: disabled ? 'not-allowed' : 'pointer',
          fontSize: '0.85rem',
        }}
      >
        <strong style={{ fontFamily: 'var(--font-serif)', color: 'var(--text-headline)' }}>{label}</strong>
        <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{sublabel}</span>
      </label>
    </div>
  );
}

interface OverlayRowProps {
  id: string;
  name: string;
  year: string;
  coverageNote?: string;
  visible: boolean;
  opacity: number;
  onToggle: () => void;
  onOpacityChange: (value: number) => void;
}

function OverlayRow({ id, name, year, coverageNote, visible, opacity, onToggle, onOpacityChange }: OverlayRowProps) {
  const checkboxId = `overlay-${id}-checkbox`;
  const sliderId = `overlay-${id}-slider`;

  const handleSlider = (event: ChangeEvent<HTMLInputElement>) => {
    onOpacityChange(Number.parseFloat(event.target.value));
  };

  return (
    <div data-testid={`overlay-row-${id}`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <input
          id={checkboxId}
          type="checkbox"
          checked={visible}
          onChange={onToggle}
          data-testid={`overlay-toggle-${id}`}
          aria-label={`Show ${name} ${year} overlay`}
          style={{
            width: 16,
            height: 16,
            accentColor: 'var(--primary)',
          }}
        />
        <label
          htmlFor={checkboxId}
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            cursor: 'pointer',
            fontSize: '0.85rem',
          }}
        >
          <strong style={{ fontFamily: 'var(--font-serif)', color: 'var(--text-headline)' }}>
            {name}
          </strong>
          <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
            {year}
            {coverageNote ? ` · ${coverageNote}` : ''}
          </span>
        </label>
        <span
          aria-hidden="true"
          style={{
            fontSize: '0.7rem',
            color: 'var(--text-muted)',
            fontVariantNumeric: 'tabular-nums',
            minWidth: 32,
            textAlign: 'right',
          }}
        >
          {Math.round(opacity * 100)}%
        </span>
      </div>
      <input
        id={sliderId}
        type="range"
        min={0}
        max={1}
        step={0.05}
        value={opacity}
        onChange={handleSlider}
        disabled={!visible}
        data-testid={`overlay-opacity-${id}`}
        aria-label={`${name} ${year} opacity`}
        aria-valuenow={Math.round(opacity * 100)}
        aria-valuemin={0}
        aria-valuemax={100}
        style={{
          width: '100%',
          marginTop: 'var(--space-1)',
          accentColor: 'var(--primary)',
          opacity: visible ? 1 : 0.4,
          cursor: visible ? 'pointer' : 'not-allowed',
        }}
      />
    </div>
  );
}
